import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useEditor } from '../../context/EditorContext';
import { useRulerTextTarget, TextRulerBand, TextRulerMarkers, BaselineRulerMarkers } from './RulerMarkers';
import { snapGuidePosition } from '../../lib/guideSnap';

export const Ruler = ({ orientation, length, zoom, scrollOffset = 0, unit, cursorPosition, workspacePadding = 288 }) => {
  const { dispatch, rawDispatch, stateRef } = useEditor();
  const send = rawDispatch || dispatch;
  const isHorizontal = orientation === 'horizontal';
  const RULER_SIZE = 24;
  const DPI = 96;
  // Publisher-style markers: tab stops / indents of the selected text frame (horizontal), baseline guides (vertical)
  const textTarget = useRulerTextTarget();
  const toScreen = (canvasPx) => canvasPx * zoom + workspacePadding - scrollOffset;
  const toCanvas = (screenPx) => (screenPx + scrollOffset - workspacePadding) / zoom;
  
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState(null);
  const rulerRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragPositionRef = useRef(null);
  
  // Calculate tick marks
  const ticks = useMemo(() => {
    let majorInterval, minorInterval, labelFormat;
    
    switch (unit) {
      case 'cm':
        majorInterval = DPI / 2.54; // ~37.8px per cm
        minorInterval = majorInterval / 10; // mm
        labelFormat = (canvasPx) => Math.round(canvasPx / majorInterval);
        break;
      case 'px':
        majorInterval = 100;
        minorInterval = 10;
        labelFormat = (canvasPx) => Math.round(canvasPx);
        break;
      case 'pt':
        majorInterval = DPI; // 72 pt = 1 in = 96 px, labelled 0, 72, 144 …
        minorInterval = DPI / 6; // 12 pt
        labelFormat = (canvasPx) => Math.round((canvasPx * 72) / DPI);
        break;
      case 'in':
      default:
        majorInterval = DPI; // 96px per inch
        minorInterval = DPI / 8; // 1/8 inch = 12px
        labelFormat = (canvasPx) => Math.round(canvasPx / majorInterval);
        break;
    }
    
    const result = [];
    
    // Calculate visible range in canvas coordinates
    // scrollOffset is how much the workspace has scrolled
    const visibleStartCanvasPx = (scrollOffset - workspacePadding) / zoom;
    const visibleEndCanvasPx = visibleStartCanvasPx + 2000 / zoom; // Approximate visible width
    
    // Round to nearest major interval for cleaner labels
    const startCanvasPx = Math.floor(visibleStartCanvasPx / majorInterval) * majorInterval - majorInterval;
    const endCanvasPx = Math.ceil(visibleEndCanvasPx / majorInterval) * majorInterval + majorInterval;
    
    for (let canvasPx = startCanvasPx; canvasPx <= endCanvasPx; canvasPx += minorInterval) {
      const isMajor = Math.abs(canvasPx % majorInterval) < 0.001 || Math.abs(canvasPx % majorInterval - majorInterval) < 0.001;
      
      // Convert canvas position to screen position on ruler
      // screen position = (canvas position * zoom) + workspacePadding - scrollOffset
      const screenPos = canvasPx * zoom + workspacePadding - scrollOffset;
      
      if (screenPos >= -50 && screenPos < 3000) {
        result.push({
          position: screenPos,
          isMajor,
          label: isMajor ? labelFormat(canvasPx) : null,
          canvasPosition: canvasPx,
        });
      }
    }
    
    return result;
  }, [zoom, scrollOffset, unit, workspacePadding, DPI]);
  
  const handleMouseMoveRef = useRef(null);
  const handleMouseUpRef = useRef(null);
  
  // A guide pulled out of the horizontal ruler is vertical (and vice versa); while it is pulled the page shows a dashed
  // preview with the exact position next to the cursor (GuideDragOverlay), snapped like a guide drag (lib/guideSnap).
  const resolveDrag = (e) => {
    const rect = rulerRef.current.getBoundingClientRect();
    const screenPos = isHorizontal ? e.clientX - rect.left : e.clientY - rect.top;
    const raw = (screenPos + scrollOffset - workspacePadding) / zoom;
    const orientation = isHorizontal ? 'vertical' : 'horizontal';
    const st = stateRef?.current || {};
    const pageEl = document.querySelector('[data-testid="canvas-page"]');
    const pr = pageEl?.getBoundingClientRect();
    const along = pr ? (isHorizontal ? (e.clientY - pr.top) / zoom : (e.clientX - pr.left) / zoom) : null;
    const { position, snap } = snapGuidePosition(raw, { orientation, guides: st.guides || [], pageSize: length, unit, zoom, free: e.altKey });
    // still over (or beyond) the ruler itself — the pointer never reached the page
    const onRuler = isHorizontal ? e.clientY <= rect.bottom : e.clientX <= rect.right;
    return { raw, position, snap, along, orientation, onRuler };
  };

  useEffect(() => {
    handleMouseMoveRef.current = (e) => {
      if (!isDraggingRef.current || !rulerRef.current) return;
      const r = resolveDrag(e);
      dragPositionRef.current = r.position;
      setDragPosition(r.position);
      send({ type: 'SET_GUIDE_DRAG', payload: { id: null, orientation: r.orientation, position: r.position, along: r.along, snap: r.snap, preview: true } });
    };
    
    handleMouseUpRef.current = (e) => {
      if (!isDraggingRef.current || !rulerRef.current) return;
      const r = resolveDrag(e);
      send({ type: 'SET_GUIDE_DRAG', payload: null });
      
      // Create the guide where it was dropped — unless it was let go back over the ruler / off the page (changed mind)
      if (!r.onRuler && r.raw >= -20 && r.raw <= length + 20) {
        dispatch({
          type: 'ADD_GUIDE',
          payload: {
            id: Date.now().toString(),
            orientation: r.orientation,
            position: Math.max(0, Math.min(r.position, length)),
          },
        });
      }
      
      isDraggingRef.current = false;
      dragPositionRef.current = null;
      setIsDragging(false);
      setDragPosition(null);
      
      document.removeEventListener('mousemove', handleMouseMoveRef.current);
      document.removeEventListener('mouseup', handleMouseUpRef.current);
    };
  });
  
  const handleMouseDown = (e) => {
    e.preventDefault();
    isDraggingRef.current = true;
    setIsDragging(true);
    
    const rect = rulerRef.current.getBoundingClientRect();
    const screenPos = isHorizontal ? e.clientX - rect.left : e.clientY - rect.top;
    const canvasPos = (screenPos + scrollOffset - workspacePadding) / zoom;
    
    dragPositionRef.current = canvasPos;
    setDragPosition(canvasPos);
    
    document.addEventListener('mousemove', handleMouseMoveRef.current);
    document.addEventListener('mouseup', handleMouseUpRef.current);
  };
  
  // Cursor tracker - convert cursor screen position to ruler position
  // cursorPosition is screen position relative to workspace start
  const cursorTrackerScreenPos = cursorPosition !== null 
    ? cursorPosition - scrollOffset 
    : null;
  
  // Drag preview - convert canvas position back to screen position
  const dragPreviewScreenPos = dragPosition !== null 
    ? dragPosition * zoom + workspacePadding - scrollOffset 
    : null;
  
  return (
    <div
      ref={rulerRef}
      className={`ruler flex-shrink-0 ${isHorizontal ? 'h-6' : 'w-6'} bg-muted overflow-hidden relative cursor-crosshair select-none`}
      style={{
        [isHorizontal ? 'width' : 'height']: '100%',
        [isHorizontal ? 'borderBottom' : 'borderRight']: '1px solid hsl(var(--border))',
      }}
      onMouseDown={handleMouseDown}
      data-testid={`ruler-${orientation}`}
      data-keep-editing
    >
      {isHorizontal && <TextRulerBand target={textTarget} zoom={zoom} toScreen={toScreen} />}
      <svg
        className="absolute inset-0 pointer-events-none"
        style={{
          width: isHorizontal ? '100%' : RULER_SIZE,
          height: isHorizontal ? RULER_SIZE : '100%',
        }}
      >
        {ticks.map((tick, i) => {
          const tickLength = tick.isMajor ? 12 : 6;
          
          return (
            <g key={i}>
              {isHorizontal ? (
                <>
                  <line
                    x1={tick.position}
                    y1={RULER_SIZE}
                    x2={tick.position}
                    y2={RULER_SIZE - tickLength}
                    className="ruler-tick"
                    strokeWidth="1"
                  />
                  {tick.label !== null && (
                    <text
                      x={tick.position + 3}
                      y={10}
                      className="ruler-text"
                    >
                      {tick.label}
                    </text>
                  )}
                </>
              ) : (
                <>
                  <line
                    x1={RULER_SIZE}
                    y1={tick.position}
                    x2={RULER_SIZE - tickLength}
                    y2={tick.position}
                    className="ruler-tick"
                    strokeWidth="1"
                  />
                  {tick.label !== null && (
                    <text
                      x={3}
                      y={tick.position + 10}
                      className="ruler-text"
                    >
                      {tick.label}
                    </text>
                  )}
                </>
              )}
            </g>
          );
        })}
      </svg>
      
      {isHorizontal
        ? <TextRulerMarkers target={textTarget} zoom={zoom} toScreen={toScreen} toCanvas={toCanvas} rulerRef={rulerRef} unit={unit} />
        : <BaselineRulerMarkers zoom={zoom} toScreen={toScreen} toCanvas={toCanvas} rulerRef={rulerRef} pageHeight={length} />}

      {/* Cursor Tracker */}
      {cursorTrackerScreenPos !== null && !isDragging && (
        <div
          className="ruler-cursor-tracker"
          style={{
            position: 'absolute',
            backgroundColor: 'hsl(var(--primary))',
            pointerEvents: 'none',
            zIndex: 10,
            ...(isHorizontal 
              ? { left: cursorTrackerScreenPos, top: 0, width: 1, height: '100%' }
              : { top: cursorTrackerScreenPos, left: 0, height: 1, width: '100%' }
            ),
          }}
        />
      )}
      
      {/* Drag Preview Line */}
      {isDragging && dragPreviewScreenPos !== null && (
        <div
          style={{
            position: 'absolute',
            backgroundColor: 'hsl(var(--primary))',
            pointerEvents: 'none',
            zIndex: 20,
            ...(isHorizontal 
              ? { left: dragPreviewScreenPos, top: 0, width: 2, height: '100%' }
              : { top: dragPreviewScreenPos, left: 0, height: 2, width: '100%' }
            ),
          }}
        />
      )}
    </div>
  );
};
